import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { RotateCcw, Lightbulb } from 'lucide-react';
import { useTranslation } from '../../../i18n';

// Natural tile metrics at scale=1
const BASE_TILE_SIZE = 48;
const BASE_GAP = 8;
const TILES_PADDING_X = 16; // px-2 on tiles area
const ACTION_BAR_HEIGHT = 44; // px-3 py-2 + button (text-sm py-1.5)
const HINT_HEIGHT = 36; // hint bar (px-4 py-2 + text-sm)
const TILES_PADDING_Y = 24; // py-3 on tiles area

// Fisher-Yates shuffle with seed for consistent scrambling
const shuffleArray = (array, seed) => {
  const result = [...array];
  let currentIndex = result.length;

  // Simple seeded random
  const seededRandom = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };

  let seedValue = seed;
  while (currentIndex !== 0) {
    seedValue++;
    const randomIndex = Math.floor(seededRandom(seedValue) * currentIndex);
    currentIndex--;
    [result[currentIndex], result[randomIndex]] = [result[randomIndex], result[currentIndex]];
  }

  return result;
};

const AnagramElement = ({
  element,
  isEditor = false,
  studentView = false,
  currentArrangement = null,
  showResult = false,
  isCorrect = null,
  onArrangementChange,
  onCheck,
  onRetry,
  disabled = false,
  scale = 1,
  hideCheckButton = false
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    word = '',
    hint = '',
    allowRetry = false,
    tileColor = '#3d6b3d',
    actionBarColor = '#f9fafb',
    fontFamily,
    fontSize: customFontSize,
    textColor = '#ffffff',
  } = content || {};
  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';

  // Measure container so tiles scale with the bounding box when it's resized
  const containerRef = useRef(null);
  const [boxScale, setBoxScale] = useState(scale);
  const letterCount = (word || '').length || 1;

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = (width, height) => {
      if (width <= 0 || height <= 0) return;
      // Tiles + inter-tile gaps only — padding is subtracted from available width below
      const naturalRowContent = letterCount * BASE_TILE_SIZE + (letterCount - 1) * BASE_GAP;
      const availableWidth = Math.max(0, width - TILES_PADDING_X);
      // Height needed at scale=1: tile row + tiles padding + action bar (+ hint if present)
      const naturalHeight = BASE_TILE_SIZE + TILES_PADDING_Y + ACTION_BAR_HEIGHT + (hint ? HINT_HEIGHT : 0);
      const next = Math.min(availableWidth / naturalRowContent, height / naturalHeight) * scale;
      setBoxScale(Math.max(0.3, next));
    };
    update(node.clientWidth, node.clientHeight);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        update(entry.contentRect.width, entry.contentRect.height);
      }
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [scale, letterCount, hint]);

  // Floor tileSize/gap so the rendered row never overflows the available width
  // due to rounding — otherwise flex-wrap can push the last letter to a new line.
  const tileSize = Math.max(20, Math.floor(BASE_TILE_SIZE * boxScale));
  const baseFontSize = customFontSize || 20;
  const tileFontSize = Math.max(10, Math.round(baseFontSize * boxScale));
  const tileGap = Math.max(2, Math.floor(BASE_GAP * boxScale));
  const tileRadius = Math.max(4, Math.round(12 * boxScale));

  // Create scrambled letters based on element ID for consistency
  const scrambledLetters = useMemo(() => {
    if (!word) return [];
    const letters = word.toUpperCase().split('').map((letter, idx) => ({
      id: `${id}_letter_${idx}`,
      letter,
      originalIndex: idx
    }));
    // Use element ID as seed for consistent scrambling
    const seed = id ? id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : Date.now();
    return shuffleArray(letters, seed);
  }, [word, id]);

  // Local state for tile arrangement
  const [tiles, setTiles] = useState(currentArrangement || scrambledLetters);
  const [selectedTileIndex, setSelectedTileIndex] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Sync with external arrangement
  useEffect(() => {
    if (currentArrangement) {
      setTiles(currentArrangement);
    } else if (scrambledLetters.length > 0) {
      setTiles(scrambledLetters);
    }
  }, [currentArrangement, scrambledLetters]);

  // Handle tile click (select/swap mode)
  const handleTileClick = useCallback((index) => {
    if (disabled || showResult || isEditor) return;

    if (selectedTileIndex === null) {
      setSelectedTileIndex(index);
    } else if (selectedTileIndex === index) {
      setSelectedTileIndex(null);
    } else {
      // Swap tiles
      const newTiles = [...tiles];
      [newTiles[selectedTileIndex], newTiles[index]] = [newTiles[index], newTiles[selectedTileIndex]];
      setTiles(newTiles);
      setSelectedTileIndex(null);
      onArrangementChange?.(newTiles);
    }
  }, [selectedTileIndex, tiles, disabled, showResult, isEditor, onArrangementChange]);

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    if (disabled || showResult || isEditor) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newTiles = [...tiles];
    [newTiles[draggedIndex], newTiles[index]] = [newTiles[index], newTiles[draggedIndex]];
    setTiles(newTiles);
    setDraggedIndex(null);
    setSelectedTileIndex(null);
    onArrangementChange?.(newTiles);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleCheck = () => {
    const currentWord = tiles.map(t => t.letter).join('');
    onCheck?.(currentWord);
  };

  const handleRetry = () => {
    setTiles(scrambledLetters);
    setSelectedTileIndex(null);
    onRetry?.();
  };

  // Check if current arrangement matches the word
  const currentWord = tiles.map(t => t.letter).join('');
  const isCurrentlyCorrect = currentWord === word.toUpperCase();

  // Editor preview mode - render same as interactive but non-functional
  if (isEditor) {
    return (
      <div
        ref={containerRef}
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        {/* Hint */}
        {hint && (
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Lightbulb size={14} className="text-amber-500" />
              {hint}
            </p>
          </div>
        )}

        {/* Tiles area */}
        <div className="flex-1 min-h-0 px-2 py-3 flex flex-col items-center justify-center overflow-hidden">
          {word ? (
            <>
              <div className="flex flex-wrap justify-center" style={{ gap: `${tileGap}px` }}>
                {scrambledLetters.map((tile, idx) => (
                  <div
                    key={tile.id}
                    className="flex items-center justify-center font-bold"
                    style={{
                      width: `${tileSize}px`,
                      height: `${tileSize}px`,
                      fontSize: `${tileFontSize}px`,
                      borderRadius: `${tileRadius}px`,
                      backgroundColor: tileColor,
                      color: textColor,
                      ...(fontFamily ? { fontFamily } : {}),
                    }}
                  >
                    {tile.letter}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-400">{t('games.anagram.enterWordInSettings')}</p>
          )}
        </div>

      </div>
    );
  }

  // Interactive/Classroom mode
  const hideChrome = true;

  const getBorderColor = () => {
    if (!showResult) return 'border-transparent';
    return isCorrect ? 'border-green-400' : 'border-red-400';
  };

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex flex-col rounded-xl overflow-hidden ${
        hideChrome ? '' : `border-2 ${getBorderColor()} shadow-sm`
      }`}
    >
      {/* Hint */}
      {hint && (
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <Lightbulb size={14} className="text-amber-500" />
            {hint}
          </p>
        </div>
      )}

      {/* Tiles area */}
      <div className="flex-1 min-h-0 px-2 py-3 flex flex-col items-center justify-center overflow-hidden">
        <div className="flex flex-wrap justify-center" style={{ gap: `${tileGap}px` }}>
          {tiles.map((tile, idx) => {
            // Check if this letter is in the correct position
            const correctWord = word.toUpperCase();
            const isLetterCorrect = tile.letter === correctWord[idx];

            // Determine background color
            let bgColor = tileColor;
            if (showResult) {
              if (isCorrect) {
                bgColor = '#22c55e'; // All green when fully correct
              } else {
                bgColor = isLetterCorrect ? '#22c55e' : '#ef4444'; // Green if correct position, red if wrong
              }
            }

            return (
              <div
                key={tile.id}
                draggable={!disabled && !showResult}
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
                onClick={() => handleTileClick(idx)}
                className={`
                  flex items-center justify-center font-bold
                  transition-all duration-150 select-none
                  ${!disabled && !showResult ? 'cursor-grab active:cursor-grabbing hover:scale-105' : 'cursor-default'}
                  ${selectedTileIndex === idx ? 'ring-4 ring-yellow-400 ring-offset-2 scale-110' : ''}
                  ${draggedIndex === idx ? 'opacity-50' : ''}
                `}
                style={{
                  width: `${tileSize}px`,
                  height: `${tileSize}px`,
                  fontSize: `${tileFontSize}px`,
                  borderRadius: `${tileRadius}px`,
                  backgroundColor: bgColor,
                  color: textColor,
                  ...(fontFamily ? { fontFamily } : {}),
                }}
              >
                {tile.letter}
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div
        className={`px-3 py-2 flex items-center justify-end gap-2 ${
          hideChrome || isActionBarTransparent ? '' : 'border-t border-gray-100'
        }`}
        style={{ backgroundColor: hideChrome ? 'transparent' : actionBarColor }}
      >
        {showResult && !isCorrect && allowRetry && (
          <button
            onClick={handleRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
          >
            <RotateCcw size={14} />
            {t('games.common.tryAgain')}
          </button>
        )}

        {!showResult && !hideCheckButton && (
          <button
            onClick={handleCheck}
            disabled={!word || tiles.length === 0}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${
              word && tiles.length > 0
                ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {t('games.common.check')}
          </button>
        )}
      </div>
    </div>
  );
};

export default AnagramElement;
